/**
 * Tour navigation helpers.
 *
 * pushState + popstate alone does NOT update DashboardContainer's React state.
 * We dispatch TOUR_NAVIGATE_EVENT so DashboardContainer (triggerTransition)
 * and AppStateManager (currentView) react before the step spotlight resolves.
 */

import type { DashboardPage } from '../modules/dashboard/components/Layout';

export const TOUR_NAVIGATE_EVENT = 'ggj:tour-navigate';

export interface TourNavigateDetail {
  path: string;
  page?: DashboardPage;
}

/** Canonical paths — keep in sync with usePageTransition / routeRegistry. */
export const TOUR_PAGE_PATHS: Partial<Record<DashboardPage, string>> = {
  dashboard:          '/dashboard',
  jobs:               '/jobs',
  agent:              '/agent',
  history:            '/history',
  'ai-companion':     '/ai-companion',
  'account-settings': '/account-settings',
  support:            '/support',
  referrals:          '/referrals',
};

const PATH_TO_PAGE: Partial<Record<string, DashboardPage>> = Object.fromEntries(
  Object.entries(TOUR_PAGE_PATHS).map(([page, path]) => [path, page as DashboardPage]),
);

async function wait(ms: number): Promise<void> {
  await new Promise<void>((r) => window.setTimeout(r, ms));
}

/** Poll until selector exists and has layout. */
export async function waitForTourTarget(
  selector: string,
  maxMs = 6000,
  intervalMs = 100,
): Promise<void> {
  const deadline = Date.now() + maxMs;
  while (Date.now() < deadline) {
    const el = document.querySelector(selector);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        await wait(150);
        return;
      }
    }
    await wait(intervalMs);
  }
}

/**
 * Navigate to a tour destination and wait for the target element to mount.
 * @param readySelector - CSS selector that must exist before the step shows.
 */
export async function navigateForTour(path: string, readySelector?: string): Promise<void> {
  const page = PATH_TO_PAGE[path];

  window.dispatchEvent(
    new CustomEvent<TourNavigateDetail>(TOUR_NAVIGATE_EVENT, {
      detail: { path, page },
    }),
  );

  // Only push URL if it changed. Do NOT dispatch popstate — that would cause
  // usePageTransition's listener to re-run getCurrentPageFromURL and trigger
  // a second setCurrentPage call that races with the TOUR_NAVIGATE_EVENT
  // handler in DashboardContainer. TOUR_NAVIGATE_EVENT alone is sufficient.
  if (window.location.pathname !== path) {
    window.history.pushState({ page }, '', path);
  }

  // Give React two animation frames to flush the state update and re-render
  // the new page before we start polling for the target element.
  await wait(400);

  if (readySelector) {
    await waitForTourTarget(readySelector, 12000);
  } else {
    await wait(400);
  }
}

export function navigateToTourPage(page: DashboardPage, readySelector?: string): Promise<void> {
  return navigateForTour(TOUR_PAGE_PATHS[page] ?? '/dashboard', readySelector);
}

